import React from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  CleanTable,
  CleanTableBody,
  CleanTableCell,
  CleanTableHead,
  CleanTableHeader,
  CleanTableRow,
} from "../ui/clean-table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { Plus, Edit, Trash2, Shield } from "lucide-react";

const MetadataList = ({
  title,
  items,
  type,
  canManage,
  onAddNew,
  onEdit,
  onDelete,
}) => {
  const getButtonText = () => {
    switch (type) {
      case "category":
        return "Category";
      case "severity":
        return "Severity";
      case "likelihood":
        return "Likelihood";
      default:
        return title.slice(0, -1);
    }
  };

  const getTableHeaders = () => {
    if (type === "severity") {
      return (
        <>
          <CleanTableHead>Level</CleanTableHead>
          <CleanTableHead>Name</CleanTableHead>
          <CleanTableHead>Description</CleanTableHead>
          <CleanTableHead>Created By</CleanTableHead>
        </>
      );
    }
    return (
      <>
        <CleanTableHead>Level</CleanTableHead>
        <CleanTableHead>Description</CleanTableHead>
        <CleanTableHead>Created By</CleanTableHead>
      </>
    );
  };

  const renderTableRow = (item) => {
    if (type === "severity") {
      return (
        <>
          <CleanTableCell>
            <Badge variant="outline" className="text-blue-600 border-blue-600">
              Level {item.value}
            </Badge>
          </CleanTableCell>
          <CleanTableCell>
            <span className="text-gray-900 font-medium">
              {item.name || "No name"}
            </span>
          </CleanTableCell>
          <CleanTableCell>
            <span className="text-gray-900">
              {item.description || "No description"}
            </span>
          </CleanTableCell>
          <CleanTableCell>
            <span className="text-gray-900">
              {item.Creator?.name || "Unknown"}
            </span>
          </CleanTableCell>
        </>
      );
    }

    return (
      <>
        <CleanTableCell>
          <Badge
            variant="outline"
            className="text-orange-600 border-orange-600"
          >
            Level {item.value}
          </Badge>
        </CleanTableCell>
        <CleanTableCell>
          <span className="text-gray-900">
            {item.description || "No description"}
          </span>
        </CleanTableCell>
        <CleanTableCell>
          <span className="text-gray-900">
            {item.Creator?.name || "Unknown"}
          </span>
        </CleanTableCell>
      </>
    );
  };

  if (items.length === 0) {
    return (
      <div className="text-center py-12">
        <Shield className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2">No {title} Found</h3>
        <p className="text-muted-foreground mb-4">
          No {title.toLowerCase()} have been created for this organization yet.
        </p>
        {canManage && (
          <Button
            size="sm"
            onClick={onAddNew}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Create Your First {getButtonText()}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-heading-3">{title}</h3>
          <p className="text-body-small text-muted-foreground">
            Organization-level {title.toLowerCase()} available to all business
            units
          </p>
        </div>
        {canManage && (
          <Button
            size="sm"
            onClick={onAddNew}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add {getButtonText()}
          </Button>
        )}
      </div>

      <TooltipProvider>
        <CleanTable>
          <CleanTableHeader>
            <CleanTableRow>
              {getTableHeaders()}
              {canManage && (
                <CleanTableHead className="text-right">Actions</CleanTableHead>
              )}
            </CleanTableRow>
          </CleanTableHeader>
          <CleanTableBody>
            {items.map((item) => (
              <CleanTableRow key={item.id}>
                {renderTableRow(item)}
                {canManage && (
                  <CleanTableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                            onClick={() => onEdit && onEdit(item)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Edit {type}</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-800 hover:bg-red-50"
                            onClick={() => onDelete && onDelete(item)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Delete {type}</TooltipContent>
                      </Tooltip>
                    </div>
                  </CleanTableCell>
                )}
              </CleanTableRow>
            ))}
          </CleanTableBody>
        </CleanTable>
      </TooltipProvider>
    </div>
  );
};

export default MetadataList;
